




































































import { Component, Prop, Provide, Mixins } from "vue-property-decorator";
import HasValidation from "@/mixins/HasValidation";
import BasketProvider from "@/components/app/global/baskets/basket/basketProvider.vue";
import ProvisioningProvider from "../provisioningProvider.vue";
import { IClient } from "@/models/clients";
import { IBasket, IBasketConvertForm, IBasketProduct } from "@/models/baskets";
import Context from "@/mixins/context";
import {
  IProvisionProviderData,
  IProvisionProviderMethods
} from "@/models/providers/provisioning";
import { DataModules } from "@/store/modules/data";
import { IProduct } from "@/models/products";
import { IAccount } from "@/models/accounts";
import { IInvoice } from "@/models/invoices";
import QuickOrderBasketView from "@/components/app/global/baskets/basket/quickOrderFlow/basketView.vue";
import { ValidationObserver } from "vee-validate";
import { BasketEvents } from "@/data/constants";

@Component({
  components: {
    BasketProvider,
    QuickOrderBasketView
  }
})
export default class QuickOrderModal extends Mixins(HasValidation, Context) {
  // Refs
  public declare $refs: {
    basketProvider: BasketProvider;
    basketView: QuickOrderBasketView;
    observer: InstanceType<typeof ValidationObserver>;
  };

  // Props

  @Prop({ default: "new" })
  public basketId!: IBasket["id"];

  @Prop({ default: null })
  public clientId!: IClient["id"];

  @Prop({ default: null })
  public accountId!: IAccount["id"];

  // Data

  public basketProvisioningProvider: ProvisioningProvider = new ProvisioningProvider(
    {
      parent: this.$parent
    }
  );

  public convertForm: IBasketConvertForm = {
    manual_import: false,
    activation_date: "",
    next_due_date: ""
  };
  public isLoading = true;
  public isProcessing = false;
  public isAddingToBasket = false;

  // Life-cycle methods

  public created() {
    this.$bus.$on(BasketEvents.BASKET_INITIATED, async (vm: BasketProvider) => {
      try {
        if (
          this.$_.get(vm, "$cuid") !==
          this.$_.get(this.$refs.basketProvider, "$cuid")
        )
          return;

        await this.$refs.basketProvider.createBasket();
        this.openSelectProductModal();
      } finally {
        this.isLoading = false;
      }
    });
  }

  public beforeDestroy() {
    this.basketProvisioningProvider.$destroy();
  }

  // Provide

  @Provide("getConvertForm")
  public getConvertForm() {
    return this.convertForm;
  }

  @Provide("setConvertForm")
  public setConvertForm(data: Partial<IBasketConvertForm>) {
    return (this.convertForm = {
      ...this.convertForm,
      ...data
    });
  }

  @Provide("provisionProviderData")
  public provisionProviderData(): IProvisionProviderData {
    return this.basketProvisioningProvider.providerData();
  }

  @Provide("provisionProviderMethods")
  public get provisionProviderMethods(): IProvisionProviderMethods {
    return this.basketProvisioningProvider.providerMethods;
  }

  // Methods

  public async openConfigureProductModal({
    product,
    basketProduct
  }: {
    product?: IProduct;
    basketProduct?: IBasketProduct;
  }) {
    const modal = await this.$store.dispatch(
      `data/${DataModules.BASKETS}/openConfigureProductModal`,
      {
        parent: this.$refs.basketProvider, // Important for provider
        props: {
          productId: product?.id,
          basketProductId: basketProduct?.id
        },
        events: {
          success: () => {
            modal.close();
          },
          "select-product": async () => {
            await modal.close();
            this.openSelectProductModal();
          }
        }
      }
    );
  }

  public openSelectProductModal() {
    this.$store.dispatch(
      `data/${DataModules.CATALOGUE}/openSelectProductsModal`,
      {
        parent: this,
        props: {
          isMultiselect: false,
          selectedProducts: [],
          filterCurrencyCode: this.$refs?.basketProvider?.currencyCode,
          isBasketContext: true
        },
        events: {
          success: async newSelectedProducts => {
            try {
              this.isAddingToBasket = true;

              const product = newSelectedProducts[0];

              const isProvisionable = await this.provisionProviderMethods.isProvisionable(
                product.id,
                this.clientId
              );

              const isConfigurable = this.$refs.basketProvider.isConfigurable(
                product
              );

              if (isProvisionable || isConfigurable) {
                this.openConfigureProductModal({ product });
              } else {
                await this.$refs.basketProvider.quickAddToBasket(product);
              }
            } finally {
              this.isAddingToBasket = false;
            }
          }
        }
      }
    );
  }

  public async createInvoice() {
    try {
      this.isProcessing = true;
      this.$refs.basketProvider.providerMethods.set("isProcessing", true);

      const isValid = await this.$refs.basketView.validateBeforeSubmit();
      if (!isValid) return;

      const basketId = this.$refs.basketProvider.providerData().basketId;

      const invoice = (await this.$store.dispatch(
        `data/${DataModules.BASKETS}/convertToInvoice`,
        {
          id: basketId,
          isClient: !this.isAdmin,
          data: {
            ...(this.convertForm.manual_import ? this.convertForm : {})
          }
        }
      )) as IInvoice;

      this.$emit("success", invoice);
      this.$emit("close");
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isProcessing = false;
      this.$refs.basketProvider.providerMethods.set("isProcessing", false);
    }
  }
}
